// Unified Mission Modal Component
// CHANGE: Removed all debug console statements for production
// Handles both creation and modification of missions with a 4-step wizard
// CREATED: Unified component replacing TemplateFormModal and TemplateEditModal
// Step 1: General information (name, description, duration, service - all required)
// Step 2: Mission tasks (at least 1 required)
// Step 3: Recurrence configuration (optional)
// Step 4: Summary and confirmation with preview
// REFACTOR: Moved direct supabase.from() services query to getServices() service function
// UPDATED: handleUpdate propagates item changes to active assignments via propagateTemplateItemsToActiveAssignments
// UPDATED: handleUpdate always propagates metadata (name, service, duration) to non-completed assignments
// UPDATED: On propagation failure, modal stays open with inline retry banner instead of closing and showing a toast
// UPDATED: handleUpdate reordered so propagation only runs after all template-level writes succeed (processItemChanges, recurrence)
// UPDATED: Block modal dismissal (close button, overlay click, Escape) while propagationStatus === 'retrying'
// UPDATED: Block wizard step navigation while loading === true (save in-flight) in addition to propagationStatus !== null
// UPDATED: Extract hasItemChanged helper; fix TaskItem interface to include text_input_label; remove as-any casts
// UPDATED: Wrap handleCloseAttempt in useCallback with propagationStatus dep to fix stale closure in Escape key handler
// UPDATED: Fix onSaved race condition — onSaved now only called after successful propagation or modal dismissal post-failure (not before setPropagationStatus); all propagation-failure dismissal paths (handleCloseAttempt, handleOverlayClick, Fermer button, retryPropagation success) now call onSaved() so the parent always refreshes
// UPDATED: Extract areItemsDifferent helper to deduplicate items-diff logic shared by hasUnsavedChanges and handleUpdate

import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { getPortalRoot } from '../../lib/portalUtils';
import { X, Save, ArrowRight, ArrowLeft, Check, AlertTriangle, RefreshCw } from 'lucide-react';
import type { TemplateWithItems } from '../../lib/templates';
import type { TaskItem, RecurrenceRule, Service } from '../../lib/supabase';
import { getServices } from '../../lib/services';
import { useMissionForm } from '../../hooks/useMissionForm';
import { MissionStep1Info } from './MissionStep1Info';
import { MissionStep2Tasks } from './MissionStep2Tasks';
import { MissionStep3Recurrence } from './MissionStep3Recurrence';
import { MissionStep4Summary } from './MissionStep4Summary';
import { WizardProgressIndicator, type WizardStep } from './WizardProgressIndicator';
import { ConfirmationDialog } from '../ui/ConfirmationDialog';
import {
  updateTemplate,
  createTemplateWithItems,
  createTaskItem,
  updateTaskItem,
  deleteTaskItem,
  reorderTaskItems,
  getTemplateWithItems,
  propagateTemplateItemsToActiveAssignments,
  propagateTemplateMetadataToActiveAssignments
} from '../../lib/templates';
import {
  getRecurrence,
  saveRecurrence,
  deleteRecurrence,
  validateRecurrenceConfig,
  type CreateRecurrenceData
} from '../../lib/recurrence';
import { useToast } from '../../hooks/useToast';

interface MissionModalProps {
  template?: TemplateWithItems | null;
  onClose: () => void;
  onSaved: () => void;
}

type WizardStepNumber = 1 | 2 | 3 | 4;

type PropagationFailure = {
  metadataFailed: boolean;
  itemsFailed: boolean;
  failedCount: number;
};

type PropagationStatus = null | 'retrying' | PropagationFailure;

const hasItemFieldsChanged = (item: TaskItem, original: TaskItem): boolean =>
  original.title !== item.title ||
  original.requires_text_input !== item.requires_text_input ||
  original.text_input_label !== item.text_input_label ||
  original.text_input_placeholder !== item.text_input_placeholder ||
  original.is_text_input_required !== item.is_text_input_required;

const hasItemChanged = (item: TaskItem, original: TaskItem, index: number): boolean =>
  hasItemFieldsChanged(item, original) || original.order_index !== index;

const areItemsDifferent = (items: TaskItem[], originalItems: TaskItem[]): boolean =>
  items.length !== originalItems.length ||
  items.some((item, index) => {
    const original = originalItems.find(o => o.id === item.id);
    if (!original) return true;
    return hasItemChanged(item, original, index);
  });

export function MissionModal({ template, onClose, onSaved }: MissionModalProps) {
  const isEditing = !!template;
  const { showToast } = useToast();

  const [currentStep, setCurrentStep] = useState<WizardStepNumber>(1);
  const [loading, setLoading] = useState(false);
  const [loadingItems, setLoadingItems] = useState(isEditing);
  const [error, setError] = useState('');
  const [recurrenceError, setRecurrenceError] = useState('');
  const [services, setServices] = useState<Service[]>([]);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
  const [propagationStatus, setPropagationStatus] = useState<PropagationStatus>(null);

  // For edit mode: track original items and recurrence
  const [originalItems, setOriginalItems] = useState<TaskItem[]>([]);
  const [originalRecurrence, setOriginalRecurrence] = useState<RecurrenceRule | null>(null);

  // Use the form hook
  const {
    name,
    setName,
    description,
    setDescription,
    duration,
    setDuration,
    serviceId,
    setServiceId,
    items,
    setItems,
    isRecurrent,
    setIsRecurrent,
    recurrenceConfig,
    setRecurrenceConfig,
    validateStep1,
    validateStep2,
    hasUnsavedChanges: formHasUnsavedChanges,
  } = useMissionForm({ template, isEditing });

  useEffect(() => {
    const loadServicesList = async () => {
      try {
        const result = await getServices();
        if (result.success && result.data) {
          setServices(result.data);
        }
      } catch (err) {
      }
    };

    loadServicesList();
  }, []);

  // Load items and recurrence if editing
  useEffect(() => {
    if (isEditing && template) {
      loadItems();
      loadRecurrence();
    }
  }, [isEditing, template?.id]);

  const loadItems = async () => {
    if (!template) return;

    try {
      setLoadingItems(true);
      const fullTemplate = await getTemplateWithItems(template.id);
      if (fullTemplate && fullTemplate.items) {
        setItems(fullTemplate.items);
        setOriginalItems(fullTemplate.items);
      }
    } catch (err) {
      setError('Erreur lors du chargement des tâches');
    } finally {
      setLoadingItems(false);
    }
  };

  const loadRecurrence = async () => {
    if (!template) return;

    try {
      const rule = await getRecurrence(template.id);
      setOriginalRecurrence(rule);
      setIsRecurrent(!!rule);

      if (rule) {
        const config = {
          preset: 'custom' as const,
          frequency: rule.frequency,
          daysOfWeek: rule.days_of_week || [],
          monthlyMode: (rule.day_of_month ? 'day' : 'week') as 'day' | 'week',
          dayOfMonth: rule.day_of_month || 1,
          weekOfMonth: rule.week_of_month || 1,
          selectedDayForWeek: rule.days_of_week?.[0] || 1,
          interval: rule.interval || 1,
          monthOfYear: rule.month_of_year || 1,
          yearlyMode: (rule.frequency === 'yearly' && rule.day_of_month ? 'day' : 'week') as 'day' | 'week',
          endMode: 'never' as const,
          endDate: null,
          endOccurrences: 10
        };
        setRecurrenceConfig(config);
      }
    } catch (err) {
    }
  };

  // Enhanced unsaved changes detection
  const hasUnsavedChanges = (): boolean => {
    if (formHasUnsavedChanges()) return true;

    // For editing, also check items and recurrence changes
    if (isEditing) {
      if (areItemsDifferent(items, originalItems)) return true;

      // Check if recurrence changed
      const hadRecurrence = !!originalRecurrence;
      if (isRecurrent !== hadRecurrence) return true;
    }

    return false;
  };

  const handleCloseAttempt = useCallback(() => {
    if (propagationStatus === 'retrying') return;
    if (propagationStatus !== null) {
      onSaved();
      return;
    }
    if (hasUnsavedChanges()) {
      setShowUnsavedChangesDialog(true);
    } else {
      onClose();
    }
  }, [propagationStatus, hasUnsavedChanges, onClose, onSaved]);

  // Escape key handler
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) {
        if (showUnsavedChangesDialog) {
          setShowUnsavedChangesDialog(false);
        } else {
          handleCloseAttempt();
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [loading, showUnsavedChangesDialog, handleCloseAttempt]);

  // Step navigation
  const handleNext = () => {
    if (currentStep === 1) {
      const validation = validateStep1();
      if (!validation.valid) {
        setError(validation.error);
        return;
      }
    }

    if (currentStep === 2) {
      // Auto-clean: Remove empty tasks before validation
      const nonEmptyTasks = items.filter(item => item.title.trim());
      if (nonEmptyTasks.length !== items.length) {
        setItems(nonEmptyTasks);
      }

      // Validate that at least one non-empty task remains
      const validation = validateStep2();
      if (!validation.valid) {
        setError(validation.error);
        return;
      }
    }

    setError('');
    setCurrentStep((prev) => Math.min(4, prev + 1) as WizardStepNumber);
  };

  const handleBack = () => {
    setError('');
    setCurrentStep((prev) => Math.max(1, prev - 1) as WizardStepNumber);
  };

  const handleStepClick = (stepNumber: number) => {
    setError('');
    setCurrentStep(stepNumber as WizardStepNumber);
  };

  // Build recurrence data from config
  const buildRecurrenceData = (): CreateRecurrenceData => {
    const { frequency, daysOfWeek, monthlyMode, dayOfMonth, weekOfMonth, selectedDayForWeek, interval, monthOfYear, yearlyMode } = recurrenceConfig;
    const data: CreateRecurrenceData = { frequency, interval };

    switch (frequency) {
      case 'daily':
        break;

      case 'weekly':
        data.days_of_week = daysOfWeek;
        break;

      case 'monthly':
        if (monthlyMode === 'day') {
          data.day_of_month = dayOfMonth;
        } else {
          data.week_of_month = weekOfMonth;
          data.days_of_week = [selectedDayForWeek];
        }
        break;

      case 'yearly':
        data.month_of_year = monthOfYear;
        if (yearlyMode === 'day') {
          data.day_of_month = dayOfMonth;
        } else {
          data.week_of_month = weekOfMonth;
          data.days_of_week = [selectedDayForWeek];
        }
        break;
    }

    return data;
  };

  // Save handler
  const handleSave = async () => {
    setError('');
    setRecurrenceError('');

    // Validate all steps
    const step1Validation = validateStep1();
    if (!step1Validation.valid) {
      setError(step1Validation.error);
      setCurrentStep(1);
      return;
    }

    const step2Validation = validateStep2();
    if (!step2Validation.valid) {
      setError(step2Validation.error);
      setCurrentStep(2);
      return;
    }

    // Validate recurrence if enabled
    if (isRecurrent) {
      const recurrenceData = buildRecurrenceData();
      const validationError = validateRecurrenceConfig(recurrenceData);
      if (validationError) {
        setRecurrenceError(validationError);
        setCurrentStep(3);
        return;
      }
    }

    setLoading(true);

    try {
      if (isEditing && template) {
        const propagation = await handleUpdate();
        const hasFailure = propagation.metadataFailed || propagation.itemsFailed;

        if (hasFailure) {
          setPropagationStatus(propagation);
          setLoading(false);
        } else {
          showToast('Mission modifiée avec succès', 'success');
          onSaved();
        }
      } else {
        await handleCreate();
        showToast('Mission créée avec succès', 'success');
        onSaved();
        onClose();
      }
    } catch (err) {
      setError('Une erreur est survenue lors de l\'enregistrement');
      setLoading(false);
    }
  };

  // Create new mission
  const handleCreate = async () => {
    const template = await createTemplateWithItems(
      {
        name: name.trim(),
        description: description.trim(),
        estimated_duration_minutes: duration,
        service_id: serviceId || null
      },
      items
    );

    // Save recurrence if configured
    if (isRecurrent) {
      const recurrenceData = buildRecurrenceData();
      await saveRecurrence(template.id, recurrenceData);
    }
  };

  // Update existing mission — returns propagation result for the caller to handle
  const handleUpdate = async (): Promise<PropagationFailure> => {
    if (!template) return { metadataFailed: false, itemsFailed: false, failedCount: 0 };

    // Detect item changes before processing (used to decide propagation)
    const itemsHaveChanged = areItemsDifferent(items, originalItems);

    // 1. Update template metadata
    await updateTemplate(template.id, {
      name: name.trim(),
      description: description.trim(),
      estimated_duration_minutes: duration,
      service_id: serviceId || null
    });

    // 2. Process item changes
    await processItemChanges();

    // 3. Handle recurrence
    if (isRecurrent) {
      const recurrenceData = buildRecurrenceData();
      await saveRecurrence(template.id, recurrenceData);
    } else if (originalRecurrence) {
      await deleteRecurrence(template.id);
    }

    let metadataFailed = false;
    let itemsFailed = false;
    let failedCount = 0;

    // 4. Propagate metadata changes to all non-completed assignments
    try {
      await propagateTemplateMetadataToActiveAssignments(template.id);
    } catch {
      metadataFailed = true;
    }

    // 5. Propagate item changes to active assignments
    if (itemsHaveChanged) {
      try {
        const result = await propagateTemplateItemsToActiveAssignments(template.id);
        if (result.failedCount > 0) {
          itemsFailed = true;
          failedCount = result.failedCount;
        }
      } catch {
        itemsFailed = true;
        failedCount = -1;
      }
    }

    return { metadataFailed, itemsFailed, failedCount };
  };

  // Process item changes for edit mode
  const processItemChanges = async () => {
    if (!template) return;

    // Find items to delete
    const itemsToDelete = originalItems.filter(
      original => !items.find(item => item.id === original.id)
    );

    // Find items to create (temp IDs)
    const itemsToCreate = items.filter(item => item.id.startsWith('temp-'));

    // Find items to update
    const itemsToUpdate = items.filter(item => {
      if (item.id.startsWith('temp-')) return false;
      const original = originalItems.find(o => o.id === item.id);
      if (!original) return false;
      return hasItemFieldsChanged(item, original);
    });

    // Check if reorder is needed
    const existingItems = items.filter(item => !item.id.startsWith('temp-'));
    const needsReorder = existingItems.some((item, index) => {
      const original = originalItems.find(o => o.id === item.id);
      return original && original.order_index !== index;
    });

    // Execute operations
    for (const item of itemsToDelete) {
      await deleteTaskItem(item.id);
    }

    for (const item of itemsToCreate) {
      await createTaskItem(template.id, item.title, item.order_index, item);
    }

    for (const item of itemsToUpdate) {
      await updateTaskItem(item.id, item);
    }

    if (needsReorder && existingItems.length > 0) {
      await reorderTaskItems(
        existingItems.map((item, index) => ({
          id: item.id,
          order_index: index
        }))
      );
    }
  };

  // Retry propagation only — does not re-save the template
  const retryPropagation = async () => {
    if (!template || typeof propagationStatus !== 'object' || propagationStatus === null) return;

    setPropagationStatus('retrying');

    let metadataFailed = false;
    let itemsFailed = false;
    let failedCount = 0;

    if (propagationStatus.metadataFailed) {
      try {
        await propagateTemplateMetadataToActiveAssignments(template.id);
      } catch {
        metadataFailed = true;
      }
    }

    if (propagationStatus.itemsFailed) {
      try {
        const result = await propagateTemplateItemsToActiveAssignments(template.id);
        if (result.failedCount > 0) {
          itemsFailed = true;
          failedCount = result.failedCount;
        }
      } catch {
        itemsFailed = true;
        failedCount = -1;
      }
    }

    if (metadataFailed || itemsFailed) {
      setPropagationStatus({ metadataFailed, itemsFailed, failedCount });
    } else {
      showToast('Mission modifiée avec succès', 'success');
      onSaved();
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target !== e.currentTarget) return;
    if (propagationStatus === 'retrying') return;
    if (propagationStatus !== null) {
      onSaved();
      return;
    }
    if (!loading && !hasUnsavedChanges()) {
      onClose();
    }
  };

  // Wizard steps configuration
  const wizardSteps: WizardStep[] = [
    { number: 1, title: 'Informations', completed: currentStep > 1 },
    { number: 2, title: 'Tâches', completed: currentStep > 2 },
    { number: 3, title: 'Récurrence', completed: currentStep > 3 },
    { number: 4, title: 'Confirmation', completed: currentStep > 4 }
  ];

  // Get service name for preview
  const getServiceName = () => {
    const service = services.find(s => s.id === serviceId);
    return service?.name;
  };

  // Show loading state while loading items in edit mode
  if (isEditing && loadingItems) {
    return createPortal(
      <div className="modal-overlay" role="dialog" aria-modal="true">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl p-8 text-center">
          <div className="text-gray-600">Chargement...</div>
        </div>
      </div>,
      getPortalRoot()
    );
  }

  return (
    <>
      {createPortal(
        <div className="modal-overlay" onClick={handleOverlayClick} role="dialog" aria-modal="true" aria-labelledby="mission-modal-title">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100 shrink-0">
              <h2 id="mission-modal-title" className="text-xl font-bold text-gray-900">
                {isEditing ? 'Modifier la mission' : 'Créer une mission'}
              </h2>
              <button
                onClick={handleCloseAttempt}
                disabled={loading || propagationStatus === 'retrying'}
                className="p-1 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
                aria-label="Fermer"
              >
                <X size={20} />
              </button>
            </div>

            {/* Wizard Progress Indicator */}
            <div className="px-6 pt-6 pb-6 border-b border-gray-200">
              <WizardProgressIndicator
                steps={wizardSteps}
                currentStep={currentStep}
                onStepClick={(propagationStatus !== null || loading) ? undefined : handleStepClick}
              />
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {/* Step 1: General Information */}
              {currentStep === 1 && (
                <MissionStep1Info
                  name={name}
                  onNameChange={setName}
                  description={description}
                  onDescriptionChange={setDescription}
                  duration={duration}
                  onDurationChange={setDuration}
                  serviceId={serviceId}
                  onServiceIdChange={setServiceId}
                  services={services}
                  loading={loading}
                />
              )}

              {/* Step 2: Tasks */}
              {currentStep === 2 && (
                <MissionStep2Tasks
                  items={items}
                  onItemsChange={setItems}
                  loading={loading}
                />
              )}

              {/* Step 3: Recurrence */}
              {currentStep === 3 && (
                <MissionStep3Recurrence
                  isRecurrent={isRecurrent}
                  onIsRecurrentChange={setIsRecurrent}
                  recurrenceConfig={recurrenceConfig}
                  onRecurrenceConfigChange={setRecurrenceConfig}
                  recurrenceError={recurrenceError}
                  loading={loading}
                  initialRecurrence={originalRecurrence}
                />
              )}

              {/* Step 4: Confirmation */}
              {currentStep === 4 && (
                <>
                  {typeof propagationStatus === 'object' && propagationStatus !== null && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
                      <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-amber-800">
                        <p className="font-semibold mb-1">Mission enregistrée — synchronisation incomplète</p>
                        <p>
                          {propagationStatus.failedCount > 0
                            ? `${propagationStatus.failedCount} attribution(s) active(s) n'ont pas reçu les modifications.`
                            : 'Certaines attributions actives n\'ont pas reçu les modifications.'}
                          {' '}Cliquez sur <strong>Réessayer</strong> pour relancer la synchronisation, ou <strong>Fermer</strong> pour ignorer.
                        </p>
                      </div>
                    </div>
                  )}

                  <MissionStep4Summary
                    name={name}
                    description={description}
                    duration={duration}
                    serviceName={getServiceName()}
                    items={items}
                    isRecurrent={isRecurrent}
                    recurrenceConfig={recurrenceConfig}
                  />
                </>
              )}
            </div>

            {/* Footer - Actions */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
              {propagationStatus !== null ? (
                <>
                  <div />
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={onSaved}
                      disabled={propagationStatus === 'retrying'}
                      className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-white transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Fermer
                    </button>
                    <button
                      type="button"
                      onClick={retryPropagation}
                      disabled={propagationStatus === 'retrying'}
                      className="flex items-center gap-2 px-5 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                      <RefreshCw size={16} className={propagationStatus === 'retrying' ? 'animate-spin' : ''} />
                      <span>{propagationStatus === 'retrying' ? 'Synchronisation...' : 'Réessayer'}</span>
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    {currentStep > 1 && (
                      <button
                        type="button"
                        onClick={handleBack}
                        disabled={loading}
                        className="flex items-center gap-2 px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                      >
                        <ArrowLeft size={16} />
                        Retour
                      </button>
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={handleCloseAttempt}
                      disabled={loading}
                      className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                      Annuler
                    </button>

                    {currentStep < 4 && (
                      <button
                        onClick={handleNext}
                        disabled={loading}
                        className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                      >
                        Suivant
                        <ArrowRight size={16} />
                      </button>
                    )}

                    {currentStep === 4 && (
                      <button
                        onClick={handleSave}
                        disabled={loading}
                        className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                      >
                        {isEditing ? <Save size={16} /> : <Check size={16} />}
                        <span>{loading ? 'En cours...' : isEditing ? 'Enregistrer' : 'Créer la mission'}</span>
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>,
        getPortalRoot()
      )}

      {/* Unsaved changes dialog */}
      {createPortal(
        <ConfirmationDialog
          isOpen={showUnsavedChangesDialog}
          title="Modifications non enregistrées"
          message="Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter sans enregistrer ?"
          confirmLabel="Quitter sans enregistrer"
          cancelLabel="Continuer à modifier"
          onConfirm={onClose}
          onCancel={() => setShowUnsavedChangesDialog(false)}
          variant="warning"
        />,
        getPortalRoot()
      )}
    </>
  );
}
